import { useState, useRef, useEffect } from 'react';

import { recentMonths, currentMonthLocal } from '../../utils/clinicTime';

interface MonthSelectorProps {
  selectedMonth: string; // Format: YYYY-MM
  onChange: (month: string) => void;
  /**
   * The clinic's current month ("YYYY-MM"), which the list counts back from. Falls back to the
   * browser's month until the dashboard has loaded and reported the clinic's timezone.
   */
  anchorMonth?: string;
}

const getMonthName = (month: string) => {
  const [year, monthNum] = month.split('-');
  const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
  return date.toLocaleDateString('es-GT', { month: 'long', year: 'numeric' });
};

export const MonthSelector = ({ selectedMonth, onChange, anchorMonth }: MonthSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const monthOptions = recentMonths(anchorMonth ?? currentMonthLocal(), 12);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (month: string) => {
    onChange(month);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-[#101822] hover:bg-slate-50 transition-colors"
      >
        <span className="material-symbols-outlined text-lg text-slate-500">calendar_month</span>
        <span className="capitalize">{getMonthName(selectedMonth)}</span>
        <svg
          className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 min-w-[200px] bg-white rounded-lg shadow-lg border border-slate-100 z-10 max-h-64 overflow-y-auto">
          {monthOptions.map((month) => (
            <button
              key={month}
              onClick={() => handleSelect(month)}
              className={`w-full text-left px-4 py-2.5 text-sm capitalize hover:bg-slate-50 transition-colors ${
                month === selectedMonth
                  ? 'bg-blue-50 text-primary font-medium'
                  : 'text-[#101822]'
              }`}
            >
              {getMonthName(month)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
